import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Idle } from '@ng-idle/core';
import { BehaviorSubject, finalize, firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UtilService } from '../util/util.service';
import { UserModel } from '../../models/user-model';
import { DialogData } from '../../models/dialog-model';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: UserModel | undefined;
  config: any;

  private isLoggedinSubject = new BehaviorSubject<boolean>(false);
  loginEvent = this.isLoggedinSubject.asObservable();
  selectedLanguage$ = new BehaviorSubject<string>('en');

  configURL = `${environment.apiUrl}/auth/config`;
  userURL = `${environment.apiUrl}/auth/user`;
  logoutURL = `${environment.apiUrl}/auth/logout`;
  extendSessionURL = `${environment.apiUrl}/auth/extend-session`;

  idleTimeOutWarningSeconds = 120;

  constructor(private router: Router, private httpClient: HttpClient,
    private idle: Idle, private utilService: UtilService, private dialog: MatDialog) {
  }

  public initiateAuth() {
    //get referrer URL from the browser
    const referrerURL = this.getReferrerURL();
    //set the referrer URL in the session storage to be used in the login page
    // only set it if it is not already set (a relogin from logout page should not override the original referrer URL)
    if (!sessionStorage.getItem('referrerURL')) {
      sessionStorage.setItem('referrerURL', referrerURL);
    }

    //get the junction path from the referer URL
    const junctionPath = this.getJunctionPath();
    this.getConfig().subscribe(configData => {
      this.config = configData;

      const path: string = window.location.pathname;

      if (path.endsWith('/logout')) {
        this.logout();
      } else {
        this.login();
        this.setupIdleTimeout(this.config.idleSeconds);
      }
    });
  }

  public login() {
    this.httpClient.get(this.userURL).subscribe((user: any) => {
      this.user = user;
      sessionStorage.setItem('brand', user.brand);
      sessionStorage.setItem('issuer', this.config.issuer);

      this.isLoggedinSubject.next(true);
      this.loadLandingPage(user);
    })
  }

  getReferrerURL() {
    const referrerURL = document.referrer;
     return referrerURL ? referrerURL : window.location.href;
  }

  getConfig(): Observable<any> {
    return this.httpClient.get(this.configURL);
  }

  async getUser() {
    // if user data is not already loaded from the login call, wait for 2 seconds for any resolver calling the getUser to load it
    if (!this.user) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    if (!this.user) {
      this.user = await firstValueFrom<UserModel>(this.httpClient.get<UserModel>(this.userURL));
    }
    this.utilService.selectBrand(this.user.brand);

    return this.user;
  }

  public logout(isBack: boolean = false) {
    this.isLoggedinSubject.next(false);

    this.httpClient.get(this.logoutURL).pipe(
      finalize(() => {
        this.user = undefined;
        if (isBack) {
          const refererURL = sessionStorage.getItem('refererURL');
          // Clear the referrerURL from session storage so that it captures the updated URL next time from differnt site
          sessionStorage.removeItem('referrerURL');
          sessionStorage.removeItem('brand');

          var backURL = this.config.backURL ? this.config.backURL : refererURL;
          window.location.href = backURL;
          return;
        } else {
          this.router.navigateByUrl('/logout');
        }
      })
    ).subscribe();

    this.idle.stop();
  }

  public appLogout(): Observable<any> {
    return this.httpClient.get(this.logoutURL);
  }

  public reLogin() {
    var currentLocation = document.location.href;
    currentLocation = currentLocation.endsWith('/logout') ? currentLocation.replace('/logout', '') : currentLocation;
    var loginRedirectURL = environment.apiUrl + '/login?continue=' + currentLocation;
    const idp = this.getIDP();
    if (idp) {
      loginRedirectURL += '&idp=' + idp;
    }
    document.location.href = loginRedirectURL;
  }

  public isAuthenticated(): boolean {
    return !!this.user;
  }

  public getIssuer() {
    return this.config.issuer;
  }

  setupIdleTimeout(idleTime: number) {
    // how long user be idle before the session timeout
    this.idle.setIdle(idleTime - this.idleTimeOutWarningSeconds);

    // seconds left before idle timeout (to display a logout warning message)
    this.idle.setTimeout(this.idleTimeOutWarningSeconds);

    // provide sources that will "interrupt" aka provide events indicating the user is active
    // this.idle.setInterrupts([new DocumentInterruptSource('keydown mousedown touchstart touchmove scroll')]);

    this.idle.onIdleStart.subscribe(() => {
      console.log(new Date() + " - User has gone Idle");
    });

    this.idle.onIdleEnd.subscribe(() => {
      console.log(new Date() + " - Idle end/reset");
      this.resetIdleTime();
    });

    // callback to display logout warning message
    this.idle.onTimeoutWarning.subscribe((remainingSeconds) => {
      if (remainingSeconds % 5 == 0) {
        console.log(new Date() + " - Session will time out in " + remainingSeconds + " seconds!");
      }

      const path: string = window.location.pathname;

      if (remainingSeconds == this.idleTimeOutWarningSeconds) {
        const dialogData: DialogData = {
          title: "",
          message: "You are about to be logged out from UDELinksAdmin application due to inactivity. Would you like to remain logged in?",
          confirmBtnLabel: `Yes, I'm still here`,
          closeBtnLabel: "No, log me out",
        };

        let dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: dialogData,
          disableClose: true
        });

        dialogRef.afterClosed().subscribe((dialogResult: any) => {
          if (dialogResult === true) {
            this.extendSession();
          } else if (dialogResult != -1) {
            // trigger a logout if it is a manual click and not auto dialog close
            this.logout();
          }
        });

        setTimeout(function () {
          // pass -1 to avoid one more logout trigger on closing the warning dialog
          if (dialogRef) dialogRef.close(-1);
        }, this.idleTimeOutWarningSeconds * 1000);
      }
    });

    this.idle.onTimeout.subscribe(() => {
      console.log(new Date() + " - User is been logged out due to idle timeout");
      this.logout();
    });

    this.resetIdleTime();
    console.log(new Date() + " - Idle timer watch is set to " + this.config.idleSeconds + " seconds");
  }

  resetIdleTime() {
    this.idle.watch();
  }

  setUnavailable() {
    this.isLoggedinSubject.next(false);
  }

  extendSession() {
    this.httpClient.get(this.extendSessionURL).subscribe((data: any) => {
      console.log('Session extended until: ' + new Date(data.session_expiry));
    })
  }

  loadLandingPage(user: UserModel) {
    const path: string = window.location.pathname;
    if (path == '/' || path == '/service-unavailable') {
      this.router.navigateByUrl('/home');
    }
  }

  getJunctionPath(): string {
    const currentPath = window.location.href;
    const jctPath = currentPath.split('/').filter(p => p.toLowerCase().includes('jct'))
    return jctPath[0] ? '/' + jctPath[0] : '';
  }

  getIDP() {
    let brand = sessionStorage.getItem('brand');
    let referrerURL = sessionStorage.getItem('referrerURL') || this.getReferrerURL();
    let ssoIDP = null;
    if (referrerURL.indexOf('audi') > -1 || brand === 'AU') {
      ssoIDP = 'accessaudi';
    } else if (referrerURL.indexOf('vw') > -1 || brand === 'VW') {
      ssoIDP = 'vwhub';
    } 
    return ssoIDP;
  }
}